import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './AdminTaxonomy.scss';

interface Ref { id: number; name: string; is_active?: number; }
interface Grupe extends Ref { pareigybes_tipas_id: number | null; }
interface Position extends Ref { sinonimu_grupe_id: number | null; pareigybes_tipas_id: number | null; fieldIds: number[]; }
interface Field extends Ref { activityIds: number[]; }

interface Tree {
  tipai: Ref[];
  grupes: Grupe[];
  positions: Position[];
  fields: Field[];
  activities: Ref[];
}

const API_URL = '/api';

const AdminTaxonomy: React.FC = () => {
  const [tree, setTree] = useState<Tree | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'positions' | 'fields'>('positions');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const fetchTree = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/taxonomy/tree`, { headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json();
      if (res.ok && json.success) { setTree(json.data); setError(null); }
      else setError(json.error || 'Nepavyko įkelti taksonomijos');
    } catch {
      setError('Klaida įkeliant taksonomiją');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTree(); }, [fetchTree]);

  const mutate = useCallback(async (body: Record<string, any>) => {
    const token = localStorage.getItem('admin_token');
    const res = await fetch(`${API_URL}/taxonomy/mutate`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await res.json();
    if (!res.ok || !json.success) throw new Error(json.error || 'Operacija nepavyko');
    await fetchTree();
    return json.data;
  }, [fetchTree]);

  const toggle = (key: string) => setExpanded(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const fieldName = useCallback((id: number) => tree?.fields.find(f => f.id === id)?.name || `#${id}`, [tree]);
  const activityName = useCallback((id: number) => tree?.activities.find(a => a.id === id)?.name || `#${id}`, [tree]);
  const tipasName = useCallback((id: number | null) => (id == null ? '—' : tree?.tipai.find(t => t.id === id)?.name || `#${id}`), [tree]);

  // Group positions by type → synonym group for the tree view.
  const positionsByGroup = useMemo(() => {
    const m = new Map<number | null, Position[]>();
    for (const p of tree?.positions || []) {
      const key = p.sinonimu_grupe_id;
      const arr = m.get(key) || [];
      arr.push(p);
      m.set(key, arr);
    }
    return m;
  }, [tree]);

  const filteredPositions = useMemo(() => {
    if (!search.trim()) return null;
    const q = search.toLowerCase();
    return (tree?.positions || []).filter(p => p.name.toLowerCase().includes(q));
  }, [search, tree]);

  if (loading) return <div className="admin-taxonomy"><p>Kraunama...</p></div>;
  if (error) return <div className="admin-taxonomy"><p className="tx-error">{error}</p></div>;
  if (!tree) return null;

  const safe = (fn: () => Promise<any>) => { fn().catch(e => setError(e.message)); };

  // ── Create handlers ────────────────────────────────────────────────────────
  const createPosition = () => {
    const name = window.prompt('Naujos pareigybės pavadinimas:');
    if (!name?.trim()) return;
    safe(() => mutate({ action: 'createPosition', name: name.trim(), grupeId: null, tipasId: null, fieldIds: [] }));
  };
  const createField = () => {
    const name = window.prompt('Naujos darbo srities pavadinimas:');
    if (!name?.trim()) return;
    safe(() => mutate({ action: 'createReference', table: 'darbo_sritis', name: name.trim() }));
  };
  const createGroup = () => {
    const name = window.prompt('Naujos sinonimų grupės pavadinimas:');
    if (!name?.trim()) return;
    safe(() => mutate({ action: 'createGroup', name: name.trim(), tipasId: null }));
  };

  return (
    <div className="admin-taxonomy">
      <div className="tx-header">
        <h1>Taksonomija</h1>
        <div className="tx-tabs">
          <button className={tab === 'positions' ? 'active' : ''} onClick={() => setTab('positions')}>Pareigybės</button>
          <button className={tab === 'fields' ? 'active' : ''} onClick={() => setTab('fields')}>Darbo sritys</button>
        </div>
      </div>

      {tab === 'positions' && (
        <div className="tx-section">
          <div className="tx-toolbar">
            <input
              type="text"
              placeholder="Ieškoti pareigybės..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="tx-search"
            />
            <button className="tx-btn" onClick={createGroup}>+ Sinonimų grupė</button>
            <button className="tx-btn tx-btn-primary" onClick={createPosition}>+ Pareigybė</button>
          </div>

          {filteredPositions ? (
            <div className="tx-flat-list">
              {filteredPositions.map(p => (
                <PositionCard key={p.id} pos={p} tree={tree} mutate={mutate} fieldName={fieldName} onError={setError} />
              ))}
              {filteredPositions.length === 0 && <p className="tx-muted">Nieko nerasta</p>}
            </div>
          ) : (
            <div className="tx-tree">
              {/* Group → positions; ungrouped positions shown under "Be grupės". */}
              {tree.grupes.map(g => {
                const key = `g-${g.id}`;
                const members = positionsByGroup.get(g.id) || [];
                return (
                  <div key={g.id} className="tx-node">
                    <div className="tx-node-head" onClick={() => toggle(key)}>
                      <span className="tx-caret">{expanded.has(key) ? '▾' : '▸'}</span>
                      <span className="tx-node-title">{g.name}</span>
                      <span className="tx-node-meta">{tipasName(g.pareigybes_tipas_id)} · {members.length}</span>
                      <select
                        value={g.pareigybes_tipas_id ?? ''}
                        onClick={e => e.stopPropagation()}
                        onChange={e => safe(() => mutate({ action: 'setGroupTipas', grupeId: g.id, tipasId: e.target.value || null }))}
                      >
                        <option value="">— tipas —</option>
                        {tree.tipai.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </select>
                    </div>
                    {expanded.has(key) && (
                      <div className="tx-node-body">
                        {members.map(p => (
                          <PositionCard key={p.id} pos={p} tree={tree} mutate={mutate} fieldName={fieldName} onError={setError} />
                        ))}
                        {members.length === 0 && <p className="tx-muted">Grupėje pareigybių nėra</p>}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Ungrouped */}
              {(() => {
                const key = 'g-none';
                const members = positionsByGroup.get(null) || [];
                if (members.length === 0) return null;
                return (
                  <div className="tx-node">
                    <div className="tx-node-head" onClick={() => toggle(key)}>
                      <span className="tx-caret">{expanded.has(key) ? '▾' : '▸'}</span>
                      <span className="tx-node-title">Be sinonimų grupės</span>
                      <span className="tx-node-meta">{members.length}</span>
                    </div>
                    {expanded.has(key) && (
                      <div className="tx-node-body">
                        {members.map(p => (
                          <PositionCard key={p.id} pos={p} tree={tree} mutate={mutate} fieldName={fieldName} onError={setError} />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {tab === 'fields' && (
        <div className="tx-section">
          <div className="tx-toolbar">
            <button className="tx-btn tx-btn-primary" onClick={createField}>+ Darbo sritis</button>
          </div>
          <div className="tx-flat-list">
            {tree.fields.map(f => (
              <div key={f.id} className="tx-card">
                <div className="tx-card-title">{f.name}</div>
                <div className="tx-chips">
                  {f.activityIds.map(aid => (
                    <span key={aid} className="tx-chip">
                      {activityName(aid)}
                      <button onClick={() => safe(() => mutate({ action: 'unlinkFieldActivity', fieldId: f.id, activityId: aid }))}>×</button>
                    </span>
                  ))}
                </div>
                <select
                  value=""
                  onChange={e => { if (e.target.value) safe(() => mutate({ action: 'linkFieldActivity', fieldId: f.id, activityId: e.target.value })); }}
                >
                  <option value="">+ Pridėti veiklos sritį</option>
                  {tree.activities.filter(a => !f.activityIds.includes(a.id)).map(a => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Position card: fields + group/type reassign ──────────────────────────────
const PositionCard: React.FC<{
  pos: Position;
  tree: Tree;
  mutate: (body: Record<string, any>) => Promise<any>;
  fieldName: (id: number) => string;
  onError: (e: string) => void;
}> = ({ pos, tree, mutate, fieldName, onError }) => {
  const safe = (fn: () => Promise<any>) => { fn().catch(e => onError(e.message)); };
  return (
    <div className="tx-card">
      <div className="tx-card-title">{pos.name}</div>
      <div className="tx-card-controls">
        <label>Grupė
          <select
            value={pos.sinonimu_grupe_id ?? ''}
            onChange={e => safe(() => mutate({ action: 'setPositionGroup', posId: pos.id, grupeId: e.target.value || null }))}
          >
            <option value="">— nėra —</option>
            {tree.grupes.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        </label>
        <label>Tipas (atsarginis)
          <select
            value={pos.pareigybes_tipas_id ?? ''}
            onChange={e => safe(() => mutate({ action: 'setPositionTipas', posId: pos.id, tipasId: e.target.value || null }))}
          >
            <option value="">— nėra —</option>
            {tree.tipai.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </label>
      </div>
      <div className="tx-chips">
        {pos.fieldIds.map(fid => (
          <span key={fid} className="tx-chip">
            {fieldName(fid)}
            <button onClick={() => safe(() => mutate({ action: 'unlinkPositionField', posId: pos.id, fieldId: fid }))}>×</button>
          </span>
        ))}
      </div>
      <select
        value=""
        onChange={e => { if (e.target.value) safe(() => mutate({ action: 'linkPositionField', posId: pos.id, fieldId: e.target.value })); }}
      >
        <option value="">+ Pridėti darbo sritį</option>
        {tree.fields.filter(f => !pos.fieldIds.includes(f.id)).map(f => (
          <option key={f.id} value={f.id}>{f.name}</option>
        ))}
      </select>
    </div>
  );
};

export default AdminTaxonomy;
